import { Input } from "antd";
import TiptapAdvancedEditor from "@/components/tiptapNovel/advanced-editor";
import { useNovelCoreFormData, useNovelActsTree } from "@/stores/novelCoreStore";
import { cn, getNovelActTreeMap, getTiptapSimpleWordCount } from "@/utils";
import { CoreEntityType, NovelSceneSchema, NovelActTree, NovelChapterSchema, NovelActSchema, NovelActTreeType } from "#/index";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ThesisOutlinePanel from "@/pages/NovelWorkspace/modules/ThesisOutlinePanel";
import { MBA_CHAPTER_CONTRACTS } from "@/lib/thesisFramework/chapterContracts";
import { shortChapterTitle } from "@/lib/thesisFramework/gateLabels";

const classNames = {
  section: `flex flex-col px-5 @2xl:flex-row @2xl:justify-center @2xl:gap-x-8 @2xl:px-12 @6xl:px-24`,
  sectionContent: "text-xl w-[min(70ch,100%-0px)]",
}

/** 正文区：左目录 + 右稿面（论文分章写作，非小说连载） */
const NovelManuscript = () => {
  const novelActsTree = useNovelActsTree();
  const novelActTreeMap = useMemo(() => getNovelActTreeMap(novelActsTree), [novelActsTree]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tocOpen = searchParams.get('toc') === '1';

  const closeTocOnMobile = () => {
    searchParams.delete('toc');
    setSearchParams(searchParams.toString(), { replace: true });
  };

  return (
    <div className="h-full flex overflow-hidden relative">
      <aside className="hidden md:flex w-[220px] shrink-0 flex-col border-r bg-[var(--tt-bg,transparent)]">
        <ThesisOutlinePanel />
      </aside>

      {tocOpen && (
        <aside className="md:hidden absolute inset-y-0 left-0 z-20 w-[min(80vw,260px)] flex flex-col border-r shadow-lg bg-[var(--tt-surface,#faf9f6)]">
          <ThesisOutlinePanel onNavigate={closeTocOnMobile} />
        </aside>
      )}
      {tocOpen && (
        <button
          type="button"
          className="md:hidden absolute inset-0 z-10 bg-black/30"
          aria-label="关闭目录"
          onClick={closeTocOnMobile}
        />
      )}

      <div className="grow h-full overflow-auto min-w-0">
        <article className="py-8">
          {novelActsTree.map(act => (
            <NovelManuscriptAct key={act.id} actData={act as NovelActTreeType<NovelActSchema>}>
              {act.children?.map(chapter => (
                <NovelManuscriptChapter key={chapter.id} chapterData={chapter as NovelActTreeType<NovelChapterSchema>}>
                  {chapter.children?.map(scene => (
                    <NovelManuscriptScene
                      key={scene.id}
                      sceneData={scene as NovelActTreeType<NovelSceneSchema>}
                      sceneDataMap={novelActTreeMap}
                    />
                  ))}
                </NovelManuscriptChapter>
              ))}
            </NovelManuscriptAct>
          ))}
          <div className="h-[30dvh]" />
        </article>
      </div>
    </div>
  );
};

const NovelManuscriptAct = ({ children, actData }: { children: React.ReactNode, actData: NovelActTreeType<NovelActSchema> }) => {
  void actData
  return <>{children}</>
}

const NovelManuscriptChapter = ({ children, chapterData }: { children: React.ReactNode, chapterData: NovelActTreeType<NovelChapterSchema> }) => {
  const { formData, onFormSubmit, onFormChange } = useNovelCoreFormData(CoreEntityType.NovelChapters, chapterData.id);
  const chNo = chapterData.autoNumber || 0
  const contract = MBA_CHAPTER_CONTRACTS.find((c) => c.chapterIndex === chNo)
  const placeholder = contract ? shortChapterTitle(contract.title) : '章标题'
  // 去掉库里「第一章 …」前缀，避免与目录序号重复成「第1章 / 第一章 绪论」
  const title = shortChapterTitle(formData?.attributes.title || '')

  return <>
    <div
      data-chapter-id={chapterData.id}
      data-chapter-no={chNo || undefined}
      className={cn(
      "nm-chapter pt-12 pb-6",
      classNames.section,
      chapterData.realIndex > 0 && "border-t border-dashed mt-12",
    )}>
      <div className={cn(classNames.sectionContent)}>
        <div className="flex items-baseline gap-2">
          {chNo > 0 ? (
            <span className="shrink-0 font-bold text-2xl lg:text-3xl/tight text-secondary tabular-nums">
              {chNo}.
            </span>
          ) : null}
          <Input
            placeholder={placeholder}
            className="font-bold text-2xl lg:text-3xl/tight leading-none w-full p-1 no-border"
            value={title}
            onChange={(e) => onFormChange({ attributes: { title: e.target.value } })}
            onBlur={() => {
              const next = shortChapterTitle(formData?.attributes.title || '')
              onFormSubmit(
                next !== formData?.attributes.title
                  ? { attributes: { title: next } }
                  : undefined,
              )
            }}
          />
        </div>
      </div>
    </div>
    {children}
  </>
}

const NovelManuscriptScene = ({ sceneData, sceneDataMap }: { sceneData: NovelActTreeType<NovelSceneSchema>, sceneDataMap: Map<string, NovelActTree> }) => {
  void sceneDataMap
  const [wordCount, setWordCount] = useState(0);
  const { formData, onFormSubmit, onFormChange } = useNovelCoreFormData(CoreEntityType.NovelScenes, sceneData.id);

  useEffect(() => {
    setWordCount(getTiptapSimpleWordCount(formData?.attributes.content || {}));
  }, [formData?.attributes.content]);

  if (!formData) return null;

  const rawTitle = (formData.attributes.title || '').trim()
  // 「正文草稿」/ 空标题：默认单节不占一行小节名
  const showSectionTitle = rawTitle && rawTitle !== '正文草稿'

  return (
    <section data-scene-id={sceneData.id} className={cn("nm-scene pb-10", classNames.section)}>
      <div className={cn('min-h-25', classNames.sectionContent)}>
        {(showSectionTitle || wordCount > 0) && (
          <div className="mb-3 flex items-baseline gap-2">
            {showSectionTitle ? (
              <Input
                placeholder={`小节 ${sceneData.realIndex + 1}`}
                className="font-semibold text-lg leading-tight grow p-0.5 no-border"
                value={rawTitle}
                onChange={(e) => onFormChange({ attributes: { title: e.target.value } })}
                onBlur={() => onFormSubmit()}
              />
            ) : (
              <span className="grow" />
            )}
            {!!wordCount && (
              <span className="shrink-0 text-xs text-tertiary tabular-nums">{wordCount} 字</span>
            )}
          </div>
        )}
        <TiptapAdvancedEditor
          value={formData?.attributes.content}
          onJsonChange={(content) => onFormChange({ attributes: { content } })}
          onBlur={() => onFormSubmit()}
        />
      </div>
    </section>
  )
}

export default NovelManuscript;
